import { useApi } from '@src/app/context/Api';
import { configuration } from '@src/entities/Event/includes/configuration';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { useCallback } from 'react';

type EventTypeCode = 'ACTION_PATH' | 'NEW_RISK' | 'OTHER' | 'REMEDIATION' | 'RULES_CHANGE';
type EventTrackerStatus = 'ACTIVE' | 'INACTIVE';

type SavePayload = {
  actualRecordCount: number;
  associatedActionPathId?: null | string;
  endDate?: null | string;
  eventId?: null | string;
  eventTrackerStatus: EventTrackerStatus;
  eventTypeCode: EventTypeCode;
  expectedRecordCount: number;
  impactedSystems: string[];
  notes: string;
  startDate: string;
  title: string;
};

type RefetchFn = () => void;

type UseSaveReturn = {
  handleSave: (payload?: SavePayload, refetch?: RefetchFn) => Promise<void>;
  mutation: unknown;
};

/**
 * Hook: useSave
 *
 * Custom React hook to handle saving an event via API.
 * Shows toast notifications for info, success, and error states.
 * Clears the event list cache and triggers a refetch on success.
 */
export const useSave = (action: string = ''): UseSaveReturn => {
  const currentAction = configuration.messages?.[action];

  const { clearApiCache, useApiMutation } = useApi('Event');
  const [SaveEvent, mutation] = useApiMutation('SaveEvent');

  const { toast } = useToast();

  /**
   * Handles saving the event, showing toasts, and clearing cache.
   */
  const handleSave = useCallback(
    async (payload: SavePayload, refetch: RefetchFn = () => {}): Promise<void> => {
      const toastId = `${action}-${configuration.slug}`;

      toast.show({
        autoClose: false,
        id: `${toastId}-info`,
        isLoading: true,
        message: currentAction?.info?.message,
        title: currentAction?.info?.title,
        variant: 'info',
      });

      await SaveEvent(payload, {
        onError: () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-error`,
            message: currentAction?.error?.message,
            title: currentAction?.error?.title,
            variant: 'error',
          });
        },
        onSuccess: async () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-success`,
            message: currentAction?.success?.message,
            title: currentAction?.success?.title,
            variant: 'success',
          });
          await clearApiCache([{ apiKey: 'Event', queryKeys: ['ListEvents'] }]);
          refetch();
        },
      });
    },
    [action, clearApiCache, currentAction, SaveEvent, toast]
  );

  return { handleSave, mutation };
};
